/**
 * GameAccordion - Accordion-based game grouping for the Value Dashboard.
 *
 * Uses useExposureFilter to group orders/positions by game.
 * "Breadth over depth" — critical info (matchup, P&L, counts, kill) is
 * always visible in the trigger; details expand on click.
 */

import { useState, useCallback } from 'react';
import { ChevronDown, ChevronUp, OctagonX } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Money } from '@/components/atoms/Money';
import { ConfirmModal } from '@/components/atoms/Modal';
import { DeltaIndicator } from '@/components/position-tracker/DeltaIndicator';
import { IndicatorPlaceholder } from '@/components/position-tracker/IndicatorPlaceholder';
import { useExposureFilter, type GameExposure } from '@/hooks/useExposureFilter';
import { useGameKill } from '@/hooks/useGameKill';
import type { KalshiOrder, Position } from '@/types';
import type { ConsolidatedGameBooks } from '@/lib/nbaConsolidated/types';
import type { GameData } from '@/lib/sportsStream/types';

interface GameAccordionProps {
  orders: KalshiOrder[];
  positions: Position[];
  positionTitleByTicker?: Record<string, string>;
  positionGameDateByTicker?: Record<string, string>;
  positionOutcomeByTicker?: Record<string, { yes?: string; no?: string }>;
  consolidatedGames?: ConsolidatedGameBooks[];
  sportsGames?: Map<string, GameData[]>;
  onCancelOrder?: (orderId: string) => Promise<void>;
  onKillAll?: () => void;
  className?: string;
}

function AccordionItem({
  exposure,
  isExpanded,
  onToggle,
  positionTitleByTicker,
  onCancelOrder,
  onKillGame,
  isKilling,
  killGameKey,
  killDone,
  killTotal,
}: {
  exposure: GameExposure;
  isExpanded: boolean;
  onToggle: () => void;
  positionTitleByTicker: Record<string, string>;
  onCancelOrder?: (orderId: string) => Promise<void>;
  onKillGame?: (gameKey: string) => Promise<void>;
  isKilling: boolean;
  killGameKey: string | null;
  killDone: number;
  killTotal: number;
}) {
  const [showKillConfirm, setShowKillConfirm] = useState(false);

  const gamePnl = exposure.positions.reduce((sum, p) => sum + p.totalPnl, 0);
  const orderCount = exposure.orders.length;
  const positionCount = exposure.positions.reduce((sum, p) => sum + Math.abs(p.quantity), 0);
  const isThisKilling = isKilling && killGameKey === exposure.gameKey;

  return (
    <div className="bg-card border-border overflow-hidden rounded-lg border">
      {/* Trigger — always visible */}
      <div className="flex items-center gap-3 px-3 py-2">
        <button
          onClick={onToggle}
          className="flex flex-1 items-center gap-3 text-left"
        >
          {isExpanded ? (
            <ChevronUp
              size={14}
              className="text-muted-foreground shrink-0"
            />
          ) : (
            <ChevronDown
              size={14}
              className="text-muted-foreground shrink-0"
            />
          )}

          {/* Matchup + date */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-bold">{exposure.awayCode}</span>
              <span className="text-muted-foreground text-[10px]">@</span>
              <span className="text-xs font-bold">{exposure.homeCode}</span>
              {exposure.startTimePt && (
                <span className="text-muted-foreground text-[10px]">{exposure.startTimePt}</span>
              )}
              <span className="text-muted-foreground text-[10px]">{exposure.date}</span>
            </div>
          </div>

          {/* P&L */}
          {positionCount > 0 && (
            <Money
              value={gamePnl / 100}
              variant="pnl"
              className="text-xs font-semibold"
            />
          )}

          {/* Order badge */}
          {orderCount > 0 && (
            <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold text-amber-400">
              {orderCount} ord
            </span>
          )}

          {/* Position badge */}
          {positionCount > 0 && (
            <span className="rounded bg-blue-500/15 px-1.5 py-0.5 text-[10px] font-bold text-blue-400">
              {positionCount.toLocaleString()} pos
            </span>
          )}
        </button>

        {/* Kill button (always visible, no expansion needed) */}
        {onKillGame && orderCount > 0 && (
          <button
            onClick={() => setShowKillConfirm(true)}
            disabled={isThisKilling}
            className={cn(
              'flex shrink-0 items-center gap-1 rounded px-2 py-1 text-[10px] font-bold uppercase tracking-wider transition-all',
              isThisKilling
                ? 'bg-muted text-muted-foreground cursor-wait'
                : 'bg-red-600/80 text-white hover:bg-red-500 active:scale-95'
            )}
          >
            <OctagonX size={11} />
            {isThisKilling ? `${killDone}/${killTotal}` : 'Kill'}
          </button>
        )}
      </div>

      {/* Kill progress */}
      {isThisKilling && killTotal > 0 && (
        <div className="bg-muted/30 mx-3 mb-1 h-1 overflow-hidden rounded-full">
          <div
            className="h-full rounded-full bg-red-500 transition-all duration-300"
            style={{ width: `${(killDone / killTotal) * 100}%` }}
          />
        </div>
      )}

      {/* Body — expanded */}
      {isExpanded && (
        <div className="border-border space-y-2 border-t px-3 py-2">
          {/* Delta */}
          <DeltaIndicator
            moneyline={exposure.moneyline}
            spread={exposure.spread}
          />

          {/* Indicator placeholders */}
          <div className="flex gap-2">
            <IndicatorPlaceholder
              label="Velocity"
              className="flex-1"
            />
            <IndicatorPlaceholder
              label="Liquidity"
              className="flex-1"
            />
          </div>

          {/* Condensed positions */}
          {exposure.positions.length > 0 && (
            <div>
              <div className="text-muted-foreground mb-0.5 text-[10px] font-medium uppercase tracking-wider">
                Positions
              </div>
              {exposure.positions.map((pos) => (
                <div
                  key={`${pos.ticker}-${pos.side}`}
                  className="flex items-center gap-2 py-0.5"
                >
                  <span
                    className={cn(
                      'w-7 text-center text-[10px] font-bold uppercase',
                      pos.side === 'yes' ? 'text-positive' : 'text-negative'
                    )}
                  >
                    {pos.side}
                  </span>
                  <span className="font-mono text-[11px]">
                    {Math.abs(pos.quantity).toLocaleString()}
                  </span>
                  <span className="text-muted-foreground text-[10px]">avg {pos.averagePrice}¢</span>
                  {positionTitleByTicker[pos.ticker] && (
                    <span className="text-muted-foreground max-w-[150px] truncate text-[10px]">
                      {positionTitleByTicker[pos.ticker]}
                    </span>
                  )}
                  <div className="ml-auto">
                    <Money
                      value={pos.totalPnl / 100}
                      variant="pnl"
                      className="text-[11px] font-semibold"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Condensed orders */}
          {exposure.orders.length > 0 && (
            <div>
              <div className="text-muted-foreground mb-0.5 text-[10px] font-medium uppercase tracking-wider">
                Orders
              </div>
              {exposure.orders.map((order) => {
                const price = order.side === 'yes' ? order.yes_price : order.no_price;
                return (
                  <div
                    key={order.order_id}
                    className="group flex items-center gap-2 py-0.5"
                  >
                    <span
                      className={cn(
                        'w-7 text-center text-[10px] font-bold uppercase',
                        order.side === 'yes' ? 'text-positive' : 'text-negative'
                      )}
                    >
                      {order.side}
                    </span>
                    <span className="font-mono text-[11px]">
                      {order.remaining_count.toLocaleString()}
                    </span>
                    <span className="text-muted-foreground text-[10px]">@ {price ?? '?'}¢</span>
                    {positionTitleByTicker[order.ticker] && (
                      <span className="text-muted-foreground max-w-[150px] truncate text-[10px]">
                        {positionTitleByTicker[order.ticker]}
                      </span>
                    )}
                    {onCancelOrder && (
                      <button
                        onClick={() => onCancelOrder(order.order_id)}
                        className="text-muted-foreground hover:text-destructive ml-auto text-[10px] opacity-0 transition-opacity group-hover:opacity-100"
                        title="Cancel order"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <ConfirmModal
        isOpen={showKillConfirm}
        onClose={() => setShowKillConfirm(false)}
        onConfirm={() => {
          onKillGame?.(exposure.gameKey);
          setShowKillConfirm(false);
        }}
        title={`Kill ${exposure.awayCode} @ ${exposure.homeCode}`}
        message={`Cancel all ${orderCount} open orders for this game?`}
        confirmLabel="Kill Orders"
        cancelLabel="Keep"
        variant="danger"
      />
    </div>
  );
}

export function GameAccordion({
  orders,
  positions,
  positionTitleByTicker = {},
  positionGameDateByTicker = {},
  positionOutcomeByTicker: _positionOutcomeByTicker = {},
  consolidatedGames,
  sportsGames,
  onCancelOrder,
  className,
}: GameAccordionProps) {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [killingGameKey, setKillingGameKey] = useState<string | null>(null);

  const exposures = useExposureFilter({
    orders,
    positions,
    positionGameDateByTicker,
    consolidatedGames,
    sportsGames,
  });

  const cancelHandler = useCallback(
    async (orderId: string) => {
      await onCancelOrder?.(orderId);
    },
    [onCancelOrder]
  );

  const gameKill = useGameKill(orders, cancelHandler);

  const handleKillGame = useCallback(
    async (gameKey: string) => {
      const exposure = exposures.find((e) => e.gameKey === gameKey);
      if (!exposure) return;
      setKillingGameKey(gameKey);
      await gameKill.killTickers([...exposure.tickers]);
      setKillingGameKey(null);
    },
    [exposures, gameKill]
  );

  const toggleKey = useCallback((key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  if (exposures.length === 0) return null;

  return (
    <div className={cn('space-y-2', className)}>
      <div className="text-muted-foreground flex items-center justify-between px-1 text-[10px] font-medium uppercase tracking-wider">
        <span>Active Game Exposure ({exposures.length})</span>
      </div>
      {exposures.map((exposure) => (
        <AccordionItem
          key={exposure.gameKey}
          exposure={exposure}
          isExpanded={expandedKeys.has(exposure.gameKey)}
          onToggle={() => toggleKey(exposure.gameKey)}
          positionTitleByTicker={positionTitleByTicker}
          onCancelOrder={onCancelOrder}
          onKillGame={handleKillGame}
          isKilling={gameKill.isKilling}
          killGameKey={killingGameKey}
          killDone={gameKill.killProgress?.done ?? 0}
          killTotal={gameKill.killProgress?.total ?? 0}
        />
      ))}
    </div>
  );
}
